'use client'

import React from 'react'
import { useFormStatus } from 'react-dom'
import { MinimalisticMagnifer } from 'solar-icons'
import { cn } from '@/lib/utils'

import {
  Button,
  KeyChip,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui'

import FloatingKeyboard from './keyboard'

interface InputFormProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: React.ReactNode
  submitLabel?: string
  submitPendingLabel?: string
  onChangeValue?: (value: string) => void
  noSearch?: boolean
}

export const InputForm = React.forwardRef<HTMLInputElement, InputFormProps>(
  (
    {
      className,
      icon,
      type = 'text',
      submitLabel,
      submitPendingLabel,
      onChangeValue,
      value,
      noSearch,
      ...props
    },
    ref,
  ) => {
    const { pending } = useFormStatus()
    const [inputValue, setInputValue] = React.useState(value || '')

    const handleInputChange = (value: string) => {
      setInputValue(value)
      onChangeValue?.(value)
    }

    const handleKeyPress = (value: string) => {
      if (value === '⌫') {
        const newValue = String(inputValue).slice(0, -1)
        setInputValue(newValue)
        onChangeValue?.(newValue)
      } else if (value === 'clear') {
        setInputValue('')
        onChangeValue?.('')
      } else {
        setInputValue((prev) => prev + value)
        onChangeValue?.(inputValue + value)
      }
    }

    return (
      <div
        className={cn(
          'flex w-full flex-col gap-1 overflow-hidden rounded-lg border border-gray-100 bg-white px-1 ring-gray-700 focus-within:bg-white focus-within:ring-2 lg:h-[52px] lg:flex-row lg:items-center dark:border-gray-700 dark:bg-gray-900',
          className,
        )}
      >
        <Popover>
          <PopoverTrigger asChild>
            <div className="flex h-full w-full items-center py-2 lg:py-0">
              {!noSearch && (
                <span className="shrink-0 px-2">
                  {icon || (
                    <MinimalisticMagnifer
                      className="size-4"
                      strokeWidth={1.5}
                    />
                  )}
                </span>
              )}

              <input
                type={type}
                ref={ref}
                placeholder="Enter Product name, Barcode, or SKU"
                className={cn(
                  'h-full flex-grow bg-transparent text-sm font-normal placeholder-gray-400 focus-visible:outline-none',
                )}
                autoCorrect="off"
                spellCheck={false}
                value={inputValue}
                onChange={(e) => handleInputChange(e.target.value)}
                {...props}
              />
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-full p-0" sideOffset={4} align="start">
            <FloatingKeyboard
              onKeyPress={handleKeyPress}
              type={type}
              isNumberOnly={type === 'number'}
            />
          </PopoverContent>
        </Popover>
        {submitLabel && (
          <div className="flex h-full py-1">
            <Button
              disabled={pending}
              aria-disabled={pending}
              className="h-full gap-2 rounded-md pr-2"
              tabIndex={-1}
              type="submit"
            >
              <span>
                {pending
                  ? (submitPendingLabel ?? 'Searching')
                  : (submitLabel ?? 'Search')}
              </span>
              <KeyChip variant="lighter" shortcut="enter" className="h-full" />
            </Button>
          </div>
        )}
      </div>
    )
  },
)

InputForm.displayName = 'InputForm'
